import { formatReceiptTime } from './submission-receipts';
import { isSettingsAccessExpired } from './settings-privacy';

/**
 * What a share link shares, and under which policy (audit E03, share half).
 *
 * Built only from the link record the API already returns (`CvShareLink`):
 * the CV, who can open it, whether phone and email are included, downloads,
 * expiry, creation time and open count. The share-link payload carries no
 * language or revision yet (D06 / C10), so neither is shown: a receipt states
 * what is known and never fills a gap with today's CV.
 */

/** Every copy key this module hands to a renderer (common namespace). */
export const SHARE_LINK_DETAIL_COPY_KEYS = [
  'shareLinkDetails.title',
  'shareLinkDetails.fields.cv',
  'shareLinkDetails.fields.access',
  'shareLinkDetails.fields.contact',
  'shareLinkDetails.fields.download',
  'shareLinkDetails.fields.expiry',
  'shareLinkDetails.fields.created',
  'shareLinkDetails.fields.opened',
  'shareLinkDetails.mainCv',
  'shareLinkDetails.access.anyone',
  'shareLinkDetails.contact.included',
  'shareLinkDetails.contact.partial',
  'shareLinkDetails.contact.hidden',
  'shareLinkDetails.download.allowed',
  'shareLinkDetails.download.off',
  'shareLinkDetails.expiry.never',
  'shareLinkDetails.expiry.expired',
  'shareLinkDetails.opened.never',
  'shareLinkDetails.opened.count',
] as const;

export type ShareLinkDetailCopyKey =
  (typeof SHARE_LINK_DETAIL_COPY_KEYS)[number];

/** The fields of a share-link record the details read. */
export interface ShareLinkRecordLike {
  visibility?: Record<string, unknown> | null;
  allowDownload?: boolean | null;
  expiresAt?: string | null;
  accessCount?: number | null;
  createdAt?: string | null;
}

/** Which CV a link shares, when the client can name it. */
export type ShareLinkCvIdentity =
  | { kind: 'main' }
  | { kind: 'titled'; title: string };

export type ShareLinkDetailValue =
  | { key: ShareLinkDetailCopyKey; params?: Record<string, string | number> }
  | { text: string };

export type ShareLinkDetailId =
  | 'cv'
  | 'access'
  | 'contact'
  | 'download'
  | 'expiry'
  | 'created'
  | 'opened';

export interface ShareLinkDetailRow {
  id: ShareLinkDetailId;
  labelKey: ShareLinkDetailCopyKey;
  value: ShareLinkDetailValue;
  /** The value is user text (a CV title): render it bidi-auto. */
  userText?: boolean;
}

/** Props of the link details (native and web twins). */
export interface ShareLinkDetailsProps {
  link: ShareLinkRecordLike | null | undefined;
  /** Which CV the link shares; null leaves the CV row out. */
  cv: ShareLinkCvIdentity | null;
}

/**
 * The CV a share target names: the profile-backed master is the Main CV; a
 * CV row is named by its title from the owner's list. A target the list does
 * not hold (a CV shared with this user, a list not loaded yet) gets no name
 * rather than a guess.
 */
export function shareLinkCvIdentity(
  target: string | null | undefined,
  cvs: readonly { id: string; title?: string | null }[] | null | undefined,
): ShareLinkCvIdentity | null {
  if (!target || target === 'master') return { kind: 'main' };
  const title = cvs?.find((cv) => cv.id === target)?.title?.trim();
  return title ? { kind: 'titled', title } : null;
}

function contactKey(
  visibility: Record<string, unknown> | null | undefined,
): ShareLinkDetailCopyKey {
  const email = Boolean(visibility?.showEmail);
  const phone = Boolean(visibility?.showPhone);
  if (email && phone) return 'shareLinkDetails.contact.included';
  if (email || phone) return 'shareLinkDetails.contact.partial';
  return 'shareLinkDetails.contact.hidden';
}

export function buildShareLinkDetails(
  link: ShareLinkRecordLike,
  options: {
    cv: ShareLinkCvIdentity | null;
    locale: string;
    now?: number;
  },
): ShareLinkDetailRow[] {
  const now = options.now ?? Date.now();
  const rows: ShareLinkDetailRow[] = [];

  if (options.cv?.kind === 'main') {
    rows.push({
      id: 'cv',
      labelKey: 'shareLinkDetails.fields.cv',
      value: { key: 'shareLinkDetails.mainCv' },
    });
  } else if (options.cv?.kind === 'titled') {
    rows.push({
      id: 'cv',
      labelKey: 'shareLinkDetails.fields.cv',
      value: { text: options.cv.title },
      userText: true,
    });
  }

  rows.push(
    {
      id: 'access',
      labelKey: 'shareLinkDetails.fields.access',
      value: { key: 'shareLinkDetails.access.anyone' },
    },
    {
      id: 'contact',
      labelKey: 'shareLinkDetails.fields.contact',
      value: { key: contactKey(link.visibility) },
    },
    {
      id: 'download',
      labelKey: 'shareLinkDetails.fields.download',
      value: {
        key: link.allowDownload
          ? 'shareLinkDetails.download.allowed'
          : 'shareLinkDetails.download.off',
      },
    },
  );

  const expiresAt = link.expiresAt ?? null;
  rows.push({
    id: 'expiry',
    labelKey: 'shareLinkDetails.fields.expiry',
    value: !expiresAt
      ? { key: 'shareLinkDetails.expiry.never' }
      : isSettingsAccessExpired(expiresAt, now)
        ? {
            key: 'shareLinkDetails.expiry.expired',
            params: { date: formatReceiptTime(expiresAt, options.locale) },
          }
        : { text: formatReceiptTime(expiresAt, options.locale) },
  });

  if (link.createdAt) {
    rows.push({
      id: 'created',
      labelKey: 'shareLinkDetails.fields.created',
      value: { text: formatReceiptTime(link.createdAt, options.locale) },
    });
  }

  const opens =
    typeof link.accessCount === 'number' && Number.isFinite(link.accessCount)
      ? Math.max(0, Math.floor(link.accessCount))
      : null;
  if (opens !== null) {
    rows.push({
      id: 'opened',
      labelKey: 'shareLinkDetails.fields.opened',
      value:
        opens === 0
          ? { key: 'shareLinkDetails.opened.never' }
          : { key: 'shareLinkDetails.opened.count', params: { count: opens } },
    });
  }

  return rows;
}
